import React, { useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { selectLanguage } from '../../store/languageSlice';
import { cvData } from '../../data/cvData';
import { translations } from '../../data/translations';
import { revealBar, revealStagger, revealUp } from '../../utils/gsapReveal';
import './Education.css';

gsap.registerPlugin(ScrollTrigger);

const Education = () => {
  const language = useSelector(selectLanguage);
  const sectionRef = useRef(null);
  const data = cvData[language];
  const t = translations[language];
  const { education, languages } = data;

  useLayoutEffect(() => {
    const scope = sectionRef.current;
    if (!scope) return;

    const ctx = gsap.context(() => {
      revealUp(scope.querySelector('.education__title'), { y: 48 });

      const eduCol = scope.querySelector('.education__col--edu');
      if (eduCol) {
        revealUp(eduCol.querySelector('.education__col-title'), { y: 28 });
        revealStagger(eduCol, '.education__card', { y: 40, stagger: 0.14 });
      }

      const langsPanel = scope.querySelector('.education__langs');
      if (langsPanel) {
        revealUp(langsPanel, { y: 36, x: 30, duration: 0.75 });
        revealStagger(langsPanel, '.education__lang-row', {
          y: 16,
          stagger: 0.1,
          start: 'top 78%',
        });
      }

      scope.querySelectorAll('.education__lang-fill').forEach((bar) => {
        revealBar(bar);
      });
    }, scope);

    return () => ctx.revert();
  }, [language]);

  return (
    <section id="education" className="education" ref={sectionRef}>
      <div className="education__inner">
        <h2 className="education__title">
          {t.education.title} {language === 'it' ? 'e Lingue' : 'and Languages'}
        </h2>

        <div className="education__grid">
          <div className="education__col education__col--edu">
            <h3 className="education__col-title">
              {language === 'it' ? 'Formazione e Percorso' : 'Education & Background'}
            </h3>

            {education.map((edu, index) => (
              <div key={index} className="education__card">
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    marginBottom: '8px',
                  }}>
                    {edu.degree}
                  </h4>
                  <p style={{
                    color: 'var(--primary-color)',
                    fontWeight: '500',
                    marginBottom: edu.grade ? '8px' : '16px',
                  }}>
                    {edu.institution}
                  </p>
                  {edu.grade && (
                    <div style={{
                      color: 'var(--text-secondary)',
                      fontSize: '0.875rem',
                      marginBottom: '16px',
                    }}>
                      {t.education.grade}:{' '}
                      <span style={{ color: 'var(--primary-color)', fontWeight: '600' }}>
                        {edu.grade}
                      </span>
                    </div>
                  )}
                </div>

                {edu.subjects && (
                  <div style={{ paddingTop: '16px' }}>
                    <p style={{
                      color: 'var(--text-secondary)',
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                      marginBottom: 0,
                    }}>
                      <strong>{t.education.subjects}:</strong> {edu.subjects}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="education__langs">
            <h3 className="education__langs-title">{t.education.languages}</h3>

            {languages.map((lang, index) => {
              const isNative =
                lang.level.toLowerCase().includes('tongue') ||
                lang.level.toLowerCase().includes('madre');

              return (
                <div key={index} className="education__lang-row">
                  <div className="education__lang-header">
                    <span className="education__lang-name">{lang.language}</span>
                    <span className="education__lang-level">{lang.level}</span>
                  </div>
                  <div className="education__lang-track">
                    <div
                      className={`education__lang-fill ${
                        isNative ? 'education__lang-fill--full' : 'education__lang-fill--high'
                      }`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Education;
